'use client';

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { DialogOptions, WithDialogProps } from './with-dialog.types';
import { cn } from '../../utils/cn';
import { Button, Divider, Modal, Text } from '../../../core-components';
import styles from './with-dialog.module.css';
import { getColorVar } from '../../../theme';
import Icon from '../../atoms/icon';
import { createPortal } from 'react-dom';

export const withDialog = <P extends WithDialogProps>(
    WrappedComponent: React.ComponentType<P>
): React.FC<Omit<P, keyof WithDialogProps>> => {
    const WithDialogComponent = (props: Omit<P, keyof WithDialogProps>) => {
        const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
        const [dialogOptions, setDialogOptions] = useState<DialogOptions>({
            content: 'Loading...',
        });

        const isActionDisplayed =
            typeof dialogOptions?.onAccept === 'function' || typeof dialogOptions?.onDismiss === 'function';

        const showDialog = () => setIsDialogVisible(true);
        const hideDialog = () => setIsDialogVisible(false);

        const handleAccept = () => {
            dialogOptions?.onAccept?.();
            hideDialog();
        };

        const handleDismiss = () => {
            dialogOptions?.onDismiss?.();
            hideDialog();
        };

        useEffect(() => {
            if (dialogOptions?.autoDismissMilliseconds && isDialogVisible) {
                const timeoutDismiss = setTimeout(() => {
                    hideDialog();
                }, dialogOptions.autoDismissMilliseconds);

                return () => {
                    clearTimeout(timeoutDismiss);
                };
            }
            return undefined;
        }, [dialogOptions?.autoDismissMilliseconds, isDialogVisible]);

        const dialogContent = (
            <Modal
                onClose={hideDialog}
                closeOnOverlayClick={true}
                open={isDialogVisible}
                showCloseButton={false}
                size={'medium'}
            >
                {/* Title with Icon */}
                {dialogOptions?.title && (
                    <div className={cn(styles.titleWrapper)}>
                        <Icon showIf={!!dialogOptions?.titleIconProps} {...dialogOptions?.titleIconProps} />
                        <Text as={'h2'} className={cn(styles.title, dialogOptions?.titleProps?.className)}>
                            {dialogOptions.title}
                        </Text>
                    </div>
                )}

                {/* Divider */}
                {dialogOptions?.title && (
                    <Divider thickness={1} orientation={'horizontal'} color={getColorVar('neutral', '20')} />
                )}

                {/* Content */}
                <div className={styles.contentWrapper}>
                    {dialogOptions.loading && (
                        <Loader2
                            data-testid="loading-indicator"
                            className="w-6 h-6 text-[var(--color-info-60)] animate-spin flex-shrink-0 mt-0.5"
                        />
                    )}
                    <Text
                        as={'p'}
                        className={cn(
                            styles.content,
                            dialogOptions.loading && 'text-left',
                            dialogOptions?.contentProps?.className
                        )}
                    >
                        {dialogOptions.content}
                    </Text>
                </div>

                {/* Actions */}
                {isActionDisplayed && (
                    <div className={cn(styles.actionWrapper)}>
                        <Button
                            showIf={!!dialogOptions?.onDismiss}
                            className={cn(styles.buttonDismiss)}
                            onClick={handleDismiss}
                            variant={'outlined'}
                            color={'var(--color-primary)'}
                        >
                            {dialogOptions.dismissLabel || 'Dismiss'}
                        </Button>

                        <Button
                            showIf={!!dialogOptions?.onAccept}
                            className={cn(styles.buttonAccept)}
                            onClick={handleAccept}
                        >
                            {dialogOptions.acceptLabel || 'Accept'}
                        </Button>
                    </div>
                )}
            </Modal>
        );

        if (typeof document === 'undefined') {
            return null;
        }

        return (
            <>
                <WrappedComponent
                    {...(props as P)}
                    showDialog={showDialog}
                    hideDialog={hideDialog}
                    setDialogOptions={setDialogOptions}
                />
                {createPortal(dialogContent, document.body)}
            </>
        );
    };

    WithDialogComponent.displayName = `WithDialog(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return WithDialogComponent;
};
